/*
  Bark & Bitter desktop menu (sections/bb-desktop-menu.liquid)

  The theme's header is left as it is. This script finds the header's
  top-level links whose titles match the panels (Shop, Recipes, About), moves
  each panel into its menu item and hides the theme's dropdown for that item.

  - Mouse: a panel opens after a short pause, so sweeping across the header
    doesn't flash panels. Moving between Shop, Recipes and About swaps panels,
    and leaving the item and its panel closes it after a short grace period.
    Clicking the link still goes to its page.
  - Touch (large tablets in landscape): the first tap opens the panel, a second
    tap follows the link, and a tap anywhere else closes it.
  - Keyboard: Enter on a closed link opens its panel and a second Enter follows
    the link. Tab moves through the panel; Escape closes it and returns to the
    link. Screen readers hear the link as expanded or collapsed.
  - Only at 1025px and wider, the theme's desktop header breakpoint.
*/
(() => {
  if (customElements.get('bb-desktop-menu')) return;

  const DESKTOP = window.matchMedia('(min-width: 1025px)');
  const SCROLL_TO_CLOSE = 40;
  const EDITOR_EVENTS = [
    'shopify:section:select',
    'shopify:section:deselect',
    'shopify:block:select',
    'shopify:block:deselect',
  ];

  // Same idea as Liquid's handle filter: "Trade & Media" -> "trade-media".
  // Written without escapes or non-ASCII characters on purpose.
  const RIGHT_QUOTE = String.fromCharCode(0x2019);
  const handleize = (text) =>
    Array.from((text || '').normalize('NFD'))
      .filter((char) => {
        const code = char.charCodeAt(0);
        return code < 0x300 || code > 0x36f; // drop accents: Cafe, not Caf
      })
      .join('')
      .toLowerCase()
      .split(RIGHT_QUOTE)
      .join('')
      .replace(/'/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  let instances = 0;

  class BbDesktopMenu extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      instances += 1;
      this.uid = `bbd${instances}`;

      this.sectionId = this.dataset.sectionId;
      this.openDelay = Number(this.dataset.openDelay) || 0;
      this.swapDelay = Number(this.dataset.swapDelay) || 0;
      this.closeDelay = Number(this.dataset.closeDelay) || 0;

      this.panels = this.panels || Array.from(this.querySelectorAll('[data-bbd-panel]'));
      this.scrim = this.scrim || this.querySelector('[data-bbd-scrim]');
      this.items = [];
      this.current = null;
      this.openTimer = 0;
      this.closeTimer = 0;
      this.lastPointer = '';
      this.warmed = false;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onSectionLoad = this.onSectionLoad.bind(this);
      this.onEditorEvent = this.onEditorEvent.bind(this);
      this.onPageHide = this.onPageHide.bind(this);

      // The stylesheet loads without blocking the page (media="print", switched
      // to "all" by its onload). Switch it here too, in case that was missed.
      document.querySelectorAll('link[href*="bb-desktop-menu.css"][media="print"]').forEach((link) => {
        link.media = 'all';
      });

      this.attach();

      DESKTOP.addEventListener('change', this.onViewportChange);
      window.addEventListener('pagehide', this.onPageHide);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      if (window.Shopify && window.Shopify.designMode) {
        EDITOR_EVENTS.forEach((type) => document.addEventListener(type, this.onEditorEvent));
      }
    }

    disconnectedCallback() {
      if (!this.connected) return;
      this.connected = false;
      this.close();
      this.detach();
      this.panels.forEach((panel) => panel.remove());
      if (this.scrim) this.scrim.remove();

      DESKTOP.removeEventListener('change', this.onViewportChange);
      window.removeEventListener('pagehide', this.onPageHide);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      EDITOR_EVENTS.forEach((type) => document.removeEventListener(type, this.onEditorEvent));
    }

    /* Wiring ------------------------------------------------------------ */

    attach() {
      const nav = document.querySelector(this.dataset.navSelector || '.header__inline-menu');
      if (!nav) return;

      this.header = nav.closest('.shopify-section') || nav.parentElement;
      const menuItems = Array.from(nav.querySelectorAll('li.menu-lv-1'));

      this.panels.forEach((panel) => {
        const li = menuItems.find((candidate) => {
          const link = candidate.querySelector(':scope > .menu-lv-1__action');
          if (!link) return false;
          const label = link.querySelector('.text') || link;
          return handleize(label.textContent) === panel.dataset.trigger;
        });
        if (!li) return;

        const link = li.querySelector(':scope > .menu-lv-1__action');
        const item = { li, link, panel };

        item.onPointerEnter = (event) => {
          if (event.pointerType === 'mouse') this.enter(item);
        };
        item.onPointerLeave = (event) => {
          if (event.pointerType === 'mouse') this.leave();
        };
        item.onPointerDown = (event) => {
          this.lastPointer = event.pointerType;
        };
        // Capture phase, so this runs before the theme's own click handler on
        // the link (assets/global.js navigates on click).
        item.onClick = (event) => this.onTriggerClick(event, item);
        item.onFocusOut = (event) => {
          if (this.current !== item) return;
          if (event.relatedTarget && !li.contains(event.relatedTarget)) this.close();
        };

        li.classList.add('bbd-item');
        li.dataset.bbdOwner = this.uid;
        li.append(panel);
        link.setAttribute('aria-expanded', 'false');
        link.setAttribute('aria-controls', panel.id);

        li.addEventListener('pointerenter', item.onPointerEnter);
        li.addEventListener('pointerleave', item.onPointerLeave);
        li.addEventListener('focusout', item.onFocusOut);
        li.addEventListener('click', item.onClick, true);
        link.addEventListener('pointerdown', item.onPointerDown);

        this.items.push(item);
      });

      if (this.scrim && this.items.length) this.header.append(this.scrim);
    }

    detach() {
      this.items.forEach((item) => {
        const { li, link, panel } = item;
        li.removeEventListener('pointerenter', item.onPointerEnter);
        li.removeEventListener('pointerleave', item.onPointerLeave);
        li.removeEventListener('focusout', item.onFocusOut);
        li.removeEventListener('click', item.onClick, true);
        link.removeEventListener('pointerdown', item.onPointerDown);

        if (li.dataset.bbdOwner === this.uid) {
          li.classList.remove('bbd-item', 'is-bbd-open');
          delete li.dataset.bbdOwner;
          link.removeAttribute('aria-expanded');
          link.removeAttribute('aria-controls');
        }
        panel.hidden = true;
        this.append(panel);
      });
      this.items = [];
      if (this.scrim) this.append(this.scrim);
    }

    /* Pointer, touch and keyboard ---------------------------------------- */

    enter(item) {
      if (!DESKTOP.matches) return;
      this.warm();
      clearTimeout(this.closeTimer);
      clearTimeout(this.openTimer);
      if (this.current === item) return;
      const delay = this.current ? this.swapDelay : this.openDelay;
      this.openTimer = setTimeout(() => this.open(item), delay);
    }

    leave() {
      clearTimeout(this.openTimer);
      if (!this.current) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.close(), this.closeDelay);
    }

    onTriggerClick(event, item) {
      const target = event.target instanceof Element ? event.target.closest('a') : null;
      if (target !== item.link) return; // A link inside the panel: let it work.

      const pointer = this.lastPointer;
      this.lastPointer = '';
      if (!DESKTOP.matches) return;
      if (pointer === 'mouse' && event.detail > 0) return; // Mouse click: go to the page.
      if (this.current === item) return; // Second tap or Enter: go to the page.

      event.preventDefault();
      event.stopPropagation();
      this.open(item);
    }

    onDocumentClick(event) {
      if (this.current && !this.current.li.contains(event.target)) this.close();
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.current) return;
      const focusInside = this.current.li.contains(document.activeElement);
      this.close({ returnFocus: focusInside });
    }

    onScroll() {
      if (Math.abs(window.scrollY - this.scrollStart) > SCROLL_TO_CLOSE) this.close();
    }

    onViewportChange() {
      if (!DESKTOP.matches) this.close();
    }

    onPageHide() {
      this.close();
    }

    /* Open and close ------------------------------------------------------ */

    open(item) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (this.current === item) return;

      const swapping = Boolean(this.current);
      if (swapping) this.hide(this.current);
      this.current = item;
      this.warm();

      // Keep the panel inside the window (it scrolls if it's taller), and stop
      // the dimmer at the bottom of the window.
      const headerBottom = this.header ? this.header.getBoundingClientRect().bottom : 0;
      const below = Math.floor(window.innerHeight - headerBottom);
      item.panel.style.setProperty('--bbd-max-h', `${Math.max(240, below)}px`);
      if (this.scrim) this.scrim.style.setProperty('--bbd-scrim-h', `${Math.max(0, below)}px`);
      item.panel.classList.toggle('bbd-panel--swap', swapping);
      item.panel.hidden = false;
      item.li.classList.add('is-bbd-open');
      item.link.setAttribute('aria-expanded', 'true');
      if (this.scrim) this.scrim.classList.add('is-visible');

      if (!swapping) {
        this.scrollStart = window.scrollY;
        document.addEventListener('click', this.onDocumentClick, true);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
    }

    hide(item) {
      item.panel.hidden = true;
      item.li.classList.remove('is-bbd-open');
      item.link.setAttribute('aria-expanded', 'false');
    }

    close({ returnFocus = false } = {}) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      const item = this.current;
      if (!item) return;

      this.hide(item);
      this.current = null;
      if (this.scrim) this.scrim.classList.remove('is-visible');
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('scroll', this.onScroll);
      if (returnFocus) item.link.focus();
    }

    // Panel photos load lazily, and hidden panels don't load them at all, so
    // start loading every panel's photos the first time someone reaches for
    // the menu. They're ready by the time a panel opens.
    warm() {
      if (this.warmed) return;
      this.warmed = true;
      this.panels.forEach((panel) => {
        panel.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      });
    }

    /* Theme editor -------------------------------------------------------- */

    onSectionLoad(event) {
      const id = event.detail && event.detail.sectionId;
      if (!id || id === this.sectionId) return;
      // Another header section was re-rendered, which replaces its menu
      // items: hook the panels back in.
      this.close();
      this.detach();
      this.attach();
    }

    onEditorEvent(event) {
      if (!event.detail || event.detail.sectionId !== this.sectionId) return;
      if (event.type === 'shopify:section:select') {
        if (!this.current && this.items[0]) this.open(this.items[0]);
      } else if (event.type === 'shopify:block:select') {
        // Match on the block id: the cards no longer sit inside this section's
        // own element, so the event's target can't be relied on.
        const blockId = event.detail.blockId;
        const hasBlock = (entry) =>
          Array.from(entry.panel.querySelectorAll('[data-bbd-block]')).some((el) => el.dataset.bbdBlock === blockId);
        const item = this.items.find(hasBlock) || this.items.find((entry) => entry.panel.contains(event.target));
        if (item) this.open(item);
      } else {
        this.close();
      }
    }
  }

  customElements.define('bb-desktop-menu', BbDesktopMenu);
})();